package ai.jessie.web.customers

import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.FormStackStyle
import ai.jessie.web.theme.Grid2Style
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.LegendStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.VisuallyHiddenStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

// Steps 4–5, A2 and A4. Submitting only asks the server to validate; nothing is saved here. The
// buttons live in the slide-over footer and address this form by [CUSTOMER_FORM_ID].
@Composable
internal fun customerForm(state: CustomerUiState, actions: CustomerActions) {
    val draft = state.draft ?: return
    Form(
        attrs = {
            id(CUSTOMER_FORM_ID)
            addEventListener("submit") {
                it.preventDefault()
                actions.preview()
            }
        }
    ) {
        Fieldset(
            attrs =
                FieldsetStyle.toAttrs {
                    if (state.busy) attr("disabled", "")
                }
        ) {
            Legend(attrs = VisuallyHiddenStyle.toAttrs()) { Text("Kundendaten") }
            Div(attrs = FormStackStyle.toAttrs()) {
                typeField(state, actions)
                customerField(
                    CustomerField(
                        "displayName",
                        "Anzeige- oder rechtlicher Name (Pflichtfeld, höchstens 200 Zeichen)",
                        draft.displayName,
                        state.problems["displayName"],
                    )
                ) { value ->
                    actions.change { it.copy(displayName = value) }
                }
                Div(attrs = Grid2Style.toAttrs()) {
                    customerField(
                        CustomerField(
                            "email",
                            "E-Mail (optional)",
                            draft.email,
                            state.problems["email"],
                        )
                    ) { value ->
                        actions.change { it.copy(email = value) }
                    }
                    customerField(
                        CustomerField(
                            "phone",
                            "Telefon (optional)",
                            draft.phone,
                            state.problems["phone"],
                        )
                    ) { value ->
                        actions.change { it.copy(phone = value) }
                    }
                }
                addressFields(state, actions)
                contactFields(state, actions)
            }
        }
    }
}

@Composable
private fun typeField(state: CustomerUiState, actions: CustomerActions) {
    val draft = state.draft ?: return
    val current = state.current
    if (current == null || current.typeChangeAvailable) {
        customerSelect(
            CustomerField("type", "Kundentyp", draft.type, state.problems["type"]),
            customerTypes,
        ) { value ->
            actions.change { it.copy(type = value) }
        }
        return
    }
    // A5, BR-070 and B3: the stored type is shown, never offered as an editable control.
    P(
        attrs =
            HintStyle.toAttrs {
                id(TYPE_ID)
                attr("tabindex", "-1")
            }
    ) {
        Text("Kundentyp: ${customerTypes[draft.type] ?: "–"}. ")
        Text(if (current.hasProjectReferences) TYPE_REFERENCED else TYPE_UNAVAILABLE)
    }
}

// A group of dynamic rows as a card inside the slide-over body; the legend is its caption. The
// card is a plain box around the borderless fieldset, so the legend is not drawn on a border.
@Composable
private fun groupCard(legend: String, content: @Composable () -> Unit) {
    Div(attrs = CardStyle.toAttrs()) {
        Fieldset(attrs = FieldsetStyle.toAttrs()) {
            Legend(attrs = LegendStyle.toAttrs()) { Text(legend) }
            Div(attrs = CardBodyStyle.toAttrs()) { content() }
        }
    }
}

@Composable
private fun smallAction(label: String, busy: Boolean, action: () -> Unit) {
    Div(attrs = RowStyle.toAttrs()) {
        themedButton(label, action, size = ButtonSize.SMALL, disabled = busy)
    }
}

@Composable
private fun addressFields(state: CustomerUiState, actions: CustomerActions) {
    val draft = state.draft ?: return
    groupCard("Adressen (optional)") {
        draft.addresses.forEachIndexed { index, row ->
            val change: ((AddressDraft) -> AddressDraft) -> Unit = { update ->
                actions.change { it.copy(addresses = it.addresses.updated(index, update)) }
            }
            val prefix = "addresses[$index]"
            val label = "Adresse ${index + 1}"
            Div(attrs = FormStackStyle.toAttrs()) {
                customerSelect(
                    CustomerField(
                        "$prefix.type",
                        "$label: Art",
                        row.type,
                        state.problems["$prefix.type"],
                    ),
                    addressTypes,
                ) { value ->
                    change { it.copy(type = value) }
                }
                addressLines(prefix, label, row, state, change)
                smallAction("$label entfernen", state.busy) {
                    actions.change {
                        it.copy(addresses = it.addresses.filterIndexed { i, _ -> i != index })
                    }
                }
            }
        }
        smallAction("Adresse hinzufügen", state.busy) {
            actions.change { it.copy(addresses = it.addresses + AddressDraft()) }
        }
    }
}

@Composable
private fun addressLines(
    prefix: String,
    label: String,
    row: AddressDraft,
    state: CustomerUiState,
    change: ((AddressDraft) -> AddressDraft) -> Unit,
) {
    fun field(name: String, text: String, value: String) =
        CustomerField("$prefix.$name", "$label: $text", value, state.problems["$prefix.$name"])
    customerField(field("line1", "Adresszeile 1", row.line1)) { value ->
        change { it.copy(line1 = value) }
    }
    customerField(field("line2", "Adresszeile 2 (optional)", row.line2)) { value ->
        change { it.copy(line2 = value) }
    }
    Div(attrs = Grid2Style.toAttrs()) {
        customerField(field("postalCode", "PLZ", row.postalCode)) { value ->
            change { it.copy(postalCode = value) }
        }
        customerField(field("city", "Ort", row.city)) { value -> change { it.copy(city = value) } }
    }
    customerField(field("countryCode", "Land (ISO-Code, zum Beispiel CH)", row.countryCode)) { value
        ->
        change { it.copy(countryCode = value) }
    }
}

@Composable
private fun contactFields(state: CustomerUiState, actions: CustomerActions) {
    val draft = state.draft ?: return
    groupCard("Kontaktpersonen (optional)") {
        P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
            Text(
                "Die Buchhaltung sieht nur Kontakte, die ausdrücklich als Rechnungskontakt " +
                    "markiert sind. Die Funktion allein gewährt keinen Zugriff."
            )
        }
        draft.contacts.forEachIndexed { index, row ->
            val change: ((ContactDraft) -> ContactDraft) -> Unit = { update ->
                actions.change { it.copy(contacts = it.contacts.updated(index, update)) }
            }
            val prefix = "contacts[$index]"
            val label = "Kontakt ${index + 1}"
            Div(attrs = FormStackStyle.toAttrs()) {
                contactLines(prefix, label, row, state, change)
                customerCheckbox(
                    "$prefix.billingContact",
                    "$label: Rechnungskontakt",
                    row.billingContact,
                ) { value ->
                    change { it.copy(billingContact = value) }
                }
                smallAction("$label entfernen", state.busy) {
                    actions.change {
                        it.copy(contacts = it.contacts.filterIndexed { i, _ -> i != index })
                    }
                }
            }
        }
        smallAction("Kontakt hinzufügen", state.busy) {
            actions.change { it.copy(contacts = it.contacts + ContactDraft()) }
        }
    }
}

@Composable
private fun contactLines(
    prefix: String,
    label: String,
    row: ContactDraft,
    state: CustomerUiState,
    change: ((ContactDraft) -> ContactDraft) -> Unit,
) {
    fun field(name: String, text: String, value: String) =
        CustomerField("$prefix.$name", "$label: $text", value, state.problems["$prefix.$name"])
    customerField(field("name", "Name", row.name)) { value -> change { it.copy(name = value) } }
    Div(attrs = Grid2Style.toAttrs()) {
        customerField(field("email", "E-Mail (optional)", row.email)) { value ->
            change { it.copy(email = value) }
        }
        customerField(field("phone", "Telefon (optional)", row.phone)) { value ->
            change { it.copy(phone = value) }
        }
    }
    customerField(field("role", "Funktion (optional)", row.role)) { value ->
        change { it.copy(role = value) }
    }
}

// Handlers update the current draft, not the one captured at composition, so several inputs
// arriving before a recomposition (autofill, paste) do not overwrite each other.
private fun <T> List<T>.updated(index: Int, update: (T) -> T): List<T> = mapIndexed { i, old ->
    if (i == index) update(old) else old
}
